import { createFileRoute, Outlet, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { EduShell, TEACHER_NAV } from "@/components/EduShell";
import { useAuth } from "@/hooks/useAuth";
import { useEducation } from "@/hooks/useEducation";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

import i18n from "@/i18n";
export const Route = createFileRoute("/teacher")({
  component: TeacherLayout,
  head: () => ({ meta: [
    { title: "Inquira Teacher Portal" },
    { name: "description", content: "Manage Inquira classrooms, assignments and teacher-controlled AI support." },
    { property: "og:title", content: "Inquira Teacher Portal" },
    { property: "og:description", content: "Manage Inquira classrooms, assignments and teacher-controlled AI support." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
});

function TeacherLayout() {
  const { user, isLoading } = useAuth();
  const { isTeacher, teacherPortalEnabled, loading, chooseRole } = useEducation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    let active = true;
    supabase
      .rpc("has_role" as never, { _user_id: user.id, _role: "super_admin" } as never)
      .then(({ data }) => { if (active) setIsAdmin(data === true); });
    return () => { active = false; };
  }, [user]);

  if (isLoading || loading) return null;

  if (!teacherPortalEnabled && !isAdmin) {
    return (
      <EduShell nav={TEACHER_NAV} title={i18n.t("edu.teacher.teacher_portal")} subtitle={i18n.t("edu.teacher.this_area_is_still_in_testing")}>
        <p className="text-sm text-ink-muted">
          Classroom tools are not switched on for your account yet. Everything else in Inquira works as normal.
        </p>
        <Link to="/" className="mt-4 inline-block text-sm font-semibold text-neon-pink">
          Back to Inquira
        </Link>
      </EduShell>
    );
  }

  if (!isTeacher) {
    return (
      <EduShell nav={TEACHER_NAV} title={i18n.t("edu.teacher.teacher_portal")} subtitle={i18n.t("edu.teacher.set_up_your_teacher_account")}>
        <p className="text-sm text-ink-muted">
          Switch your Inquira account to teaching mode to create classrooms and assignments. Your personal study
          tools stay exactly as they are.
        </p>
        <Button className="mt-4" onClick={() => chooseRole("teacher")}>
          Enable teaching mode
        </Button>
      </EduShell>
    );
  }

  return <Outlet />;
}
